/**
 * TalentLens AI - Dashboard Page
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import api from '../lib/api';
import { useAuthStore, canViewAnalytics } from '../store/auth';

export default function DashboardPage() {
  const { user } = useAuthStore();

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.getDashboard(),
    // Smart polling: faster when CVs are processing
    refetchInterval: (query) => {
      const stats = query.state.data?.data?.overview;
      const hasProcessing = (stats?.pendingCVs || 0) > 0 || (stats?.processingCVs || 0) > 0;
      // Poll every 5 seconds if processing, every 30 seconds otherwise
      return hasProcessing ? 5000 : 30000;
    },
  });

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const stats = dashboard?.data?.overview;
  const activity = dashboard?.data?.recentActivity || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.name}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total CVs"
          value={stats?.totalCVs || 0}
          subValue={`${stats?.processedCVs || 0} processed`}
          icon={<DocumentIcon />}
          color="primary"
        />
        <StatCard
          title="Pending"
          value={stats?.pendingCVs || 0}
          subValue="Awaiting processing"
          icon={<ClockIcon />}
          color="yellow"
        />
        <StatCard
          title="Active Jobs"
          value={stats?.activeJobs || 0}
          subValue={`${stats?.totalJobs || 0} total`}
          icon={<BriefcaseIcon />}
          color="green"
        />
        <StatCard
          title="Failed"
          value={stats?.failedCVs || 0}
          subValue="Need attention"
          icon={<AlertIcon />}
          color="red"
        />
      </div>

      {/* Quick Actions & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/cvs"
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <UploadIcon className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Upload CVs</p>
                <p className="text-sm text-gray-500">Add new candidate CVs</p>
              </div>
            </Link>

            <Link
              to="/jobs"
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <PlusIcon className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Create Job</p>
                <p className="text-sm text-gray-500">Post new position</p>
              </div>
            </Link>

            <Link
              to="/search"
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <SearchIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Search Candidates</p>
                <p className="text-sm text-gray-500">Find matching profiles</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          {activity.length > 0 ? (
            <div className="space-y-4">
              {activity.slice(0, 5).map((item: any) => (
                <div key={item.id} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityColor(item.action)}`}>
                    {getActivityIcon(item.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{item.user?.name || 'Unknown'}</span>
                      {' '}{formatAction(item.action)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatTime(item.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No recent activity</p>
          )}
        </div>
      </div>

      {/* Processing Stats */}
      {canViewAnalytics(user) && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Processing Overview</h2>
            <Link
              to="/analytics"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View Analytics
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Today's Uploads</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboard?.data?.activity?.todayUploads || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboard?.data?.activity?.weekUploads || 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {dashboard?.data?.processing?.successRate || 0}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({
  title,
  value,
  subValue,
  icon,
  color,
}: {
  title: string;
  value: number;
  subValue: string;
  icon: React.ReactNode;
  color: 'primary' | 'green' | 'yellow' | 'red';
}) {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          <p className="text-xs text-gray-500">{subValue}</p>
        </div>
      </div>
    </div>
  );
}

// Skeleton loader
function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-48" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-6 h-24" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 h-64" />
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 h-64" />
      </div>
    </div>
  );
}

// Helper functions
function getActivityColor(action: string): string {
  if (action.includes('upload')) return 'bg-primary-100';
  if (action.includes('delete')) return 'bg-red-100';
  if (action.includes('match')) return 'bg-green-100';
  if (action.includes('search')) return 'bg-purple-100';
  return 'bg-gray-100';
}

function getActivityIcon(action: string) {
  if (action.includes('upload')) return <UploadIcon className="w-4 h-4 text-primary-600" />;
  if (action.includes('delete')) return <TrashIcon className="w-4 h-4 text-red-600" />;
  if (action.includes('match')) return <MatchIcon className="w-4 h-4 text-green-600" />;
  if (action.includes('search')) return <SearchIcon className="w-4 h-4 text-purple-600" />;
  return <DocumentIcon className="w-4 h-4 text-gray-600" />;
}

function formatAction(action: string): string {
  const actions: Record<string, string> = {
    'cv.upload': 'uploaded a CV',
    'cv.delete': 'deleted a CV',
    'cv.view': 'viewed a CV',
    'cv.download': 'downloaded a CV',
    'job.create': 'created a job',
    'job.update': 'updated a job',
    'job.match': 'ran candidate matching',
    'search.execute': 'searched candidates',
    'search.export': 'exported search results',
    'user.login': 'logged in',
  };
  return actions[action] || action.replace('.', ' ');
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

// Icons
function DocumentIcon({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ClockIcon({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function BriefcaseIcon({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function AlertIcon({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function UploadIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function PlusIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function SearchIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function TrashIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function MatchIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
